/**
 * Query Keys Factory
 * 
 * Centralized query key management for React Query cache invalidation.
 * Using a factory pattern ensures consistent keys across queries and mutations.
 * 
 * Usage:
 * - For queries: useQuery({ queryKey: queryKeys.supporteds.list(fundId), ... })
 * - For invalidation: queryClient.invalidateQueries({ queryKey: queryKeys.supporteds.list(fundId) })
 * - For partial invalidation: queryClient.invalidateQueries({ queryKey: queryKeys.supporteds.all })
 */

export const queryKeys = {
  // Supporteds
  supporteds: {
    all: ['supporteds'] as const,
    list: (fundId: number) => [`funds/${fundId}/supporteds`] as const,
    basic: (fundId: number) => [`funds/${fundId}/supporteds/basic`] as const,
    classifications: (fundId: number) => [`funds/${fundId}/supporteds/classifications`] as const,
    detail: (fundId: number, supportedId: number) => [`funds/${fundId}/supporteds/${supportedId}`] as const,
    additionalInfo: (fundId: number, supportedId: number) => 
      [`funds/${fundId}/supporteds/${supportedId}/additional-info`] as const,
    availableClassifications: (fundId: number, supportedId: number) =>
      [`funds/${fundId}/supporteds/${supportedId}/available-classifications`] as const,
  },

  // Support Requests
  supportRequests: {
    all: ['support-requests'] as const,
    list: (fundId: number) => [`funds/${fundId}/support-requests`] as const,
    management: (tab: string) => [`management/support-requests?tab=${tab}`] as const,
    managementAll: ['management/support-requests'] as const,
    // Predicate to match all management tabs
    isManagementQuery: (queryKey: readonly unknown[]) =>
      typeof queryKey[0] === 'string' && queryKey[0].startsWith('management/support-requests?tab='),
  },

  // Goals
  goals: {
    all: ['goals'] as const,
    list: (fundId: number) => [`funds/${fundId}/goals`] as const,
    withLimits: (fundId: number, supportedId: number) => 
      [`funds/${fundId}/supporteds/${supportedId}/goals-with-limits`] as const,
    classificationLimits: (fundId: number, goalId: number) =>
      [`funds/${fundId}/goals/${goalId}/classification-goal-limits`] as const,
  },

  // Fund Balance
  fundBalance: {
    detail: (fundId: number) => [`funds/financial-data/${fundId}/balance`] as const,
    isBalanceQuery: (queryKey: readonly unknown[]) =>
      typeof queryKey[0] === 'string' && /^funds\/financial-data\/\d+\/balance$/.test(queryKey[0]),
  },

  // Transactions
  transactions: {
    all: ['transactions'] as const,
    list: (fundId: number, start: string, end: string) => 
      [`funds/financial-data/${fundId}/transactions?start=${start}&end=${end}`] as const,
  },

  // Classifications
  classifications: {
    all: ['classifications'] as const,
    list: () => ['classifications'] as const,
    management: ['management/classifications'] as const,
  },

  // Children (Management)
  children: {
    all: ['children'] as const,
    management: ['management/children'] as const,
  },

  // Payment Methods
  paymentMethods: {
    byContact: (contactId: number) => 
      [`management/support-requests/contacts/${contactId}/payment-methods`] as const,
  },

  // Receipts
  receipts: {
    detail: (id: number, defaultReceipt: boolean) => 
      [`receipts/${id}?defaultReceipt=${defaultReceipt}`] as const,
  },
  // Inquiries (Coordinator)
  inquiries: {
    all: ['inquiries'] as const,
    list: (fundId: number, contactId: number) => [`funds/${fundId}/inquiries?contactId=${contactId}`] as const,
    detail: (fundId: number, inquiryId: number, contactId: number) =>
      [`funds/${fundId}/inquiries/${inquiryId}?contactId=${contactId}`] as const,
    unreadCount: (fundId: number, contactId: number) =>
      [`funds/${fundId}/inquiries/unread-count?contactId=${contactId}`] as const,
  },

  // Inquiries (Management)
  managementInquiries: {
    all: ['management-inquiries'] as const,
    query: (query: object) => ['management-inquiries', JSON.stringify(query)] as const,
    detail: (inquiryId: number) => [`management/inquiries/${inquiryId}`] as const,
    stats: (scope: string) => [`management/inquiries/stats?scope=${scope}`] as const,
    labelStats: (query: object) => ['management-label-stats', JSON.stringify(query)] as const,
  },

  // Labels
  inquiryLabels: {
    all: ['inquiry-labels'] as const,
    list: ['management/labels'] as const,
  },

  // Departments
  departments: {
    all: ['departments'] as const,
    list: ['management/departments'] as const,
  },

  // Notifications
  inquiryNotifications: {
    all: ['inquiry-notifications'] as const,
    list: (unreadOnly: boolean) => [`management/notifications?unreadOnly=${unreadOnly}`] as const,
    unreadCount: ['management/notifications/unread-count'] as const,
  },

  // System Settings
  systemSettings: {
    all: ['system-settings'] as const,
    list: ['management/settings'] as const,
  },
} as const;

// Type helper to extract query key type
export type QueryKeys = typeof queryKeys;

